import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";

const SUPPORTED = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

function setNativeValue(el, value) {
  const proto = el instanceof HTMLTextAreaElement ? window.HTMLTextAreaElement : window.HTMLInputElement;
  const setter = Object.getOwnPropertyDescriptor(proto.prototype, "value")?.set;
  if (setter) setter.call(el, value); else el.value = value;
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

function clickByLabel(label) {
  const target = norm(label);
  if (!target) return false;
  const els = [...document.querySelectorAll("button, a, [role='button'], input[type='checkbox']")];
  const el = els.find((e) => {
    if (!e.offsetParent) return false;
    const labelText = norm(e.innerText || e.getAttribute("aria-label") || (e.getAttribute("data-testid") || "").replace(/-/g, " "));
    return labelText.includes(target);
  });
  if (el) {
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setTimeout(() => el.click(), 350);
    return true;
  }
  return false;
}

function focusByLabel(label) {
  const target = norm(label);
  const els = [...document.querySelectorAll("input:not([type='checkbox']):not([type='radio']), textarea, select")];
  const el = els.find((e) => {
    if (!e.offsetParent) return false;
    const t = norm(`${e.getAttribute("aria-label") || ""} ${e.placeholder || ""} ${(e.getAttribute("data-testid") || "").replace(/-/g, " ")} ${e.id}`);
    return t.includes(target);
  });
  if (el) {
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setTimeout(() => el.focus(), 350);
    return true;
  }
  return false;
}

export default function VoiceControl() {
  const [active, setActive] = useState(false);
  const [heard, setHeard] = useState("");
  const recRef = useRef(null);

  useEffect(() => {
    const read = () => {
      let prefs = {};
      try { prefs = JSON.parse(localStorage.getItem("sp_a11y") || "{}"); } catch { /* noop */ }
      setActive(!!prefs.voice && SUPPORTED);
    };
    read();
    window.addEventListener("sp-a11y-changed", read);
    return () => window.removeEventListener("sp-a11y-changed", read);
  }, []);

  useEffect(() => {
    if (!active || !SUPPORTED) return undefined;
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = "en-AU";
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      const text = e.results[e.results.length - 1][0].transcript.trim().toLowerCase();
      setHeard(text);
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA") && !["checkbox", "radio", "range", "number"].includes(el.type);
      if (text.startsWith("type ")) {
        if (typing) setNativeValue(el, (el.value ? `${el.value} ` : "") + text.slice(5));
      } else if (text === "clear" && typing) {
        setNativeValue(el, "");
      } else if (text.includes("scroll down")) {
        window.scrollBy({ top: 600, behavior: "smooth" });
      } else if (text.includes("scroll up")) {
        window.scrollBy({ top: -600, behavior: "smooth" });
      } else if (text.includes("top of page") || text.includes("scroll to top")) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (text.includes("bottom of page") || text.includes("scroll to bottom")) {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      } else if (text.includes("go back")) {
        window.history.back();
      } else if (/^(click|press|open|select)\s+/.test(text)) {
        clickByLabel(text.replace(/^(click|press|open|select)\s+/, ""));
      } else if (text.startsWith("focus ")) {
        focusByLabel(text.slice(6));
      } else if (typing) {
        // free dictation — whatever you say lands in the focused field
        setNativeValue(el, (el.value ? `${el.value} ` : "") + text);
      }
    };
    rec.onend = () => {
      if (recRef.current) {
        try { rec.start(); } catch { /* already started */ }
      }
    };
    rec.onerror = () => {};
    recRef.current = rec;
    try { rec.start(); } catch { /* permission prompts handled by browser */ }
    return () => {
      recRef.current = null;
      rec.onend = null;
      try { rec.stop(); } catch { /* noop */ }
    };
  }, [active]);

  if (!active) return null;

  return (
    <div data-testid="voice-control-indicator" role="status" aria-live="polite" className="fixed bottom-4 left-4 z-[90] border border-nc-blue/50 bg-black/85 backdrop-blur px-4 py-3 flex items-center gap-3 max-w-xs">
      <Mic className="w-4 h-4 text-nc-blue animate-pulse shrink-0" />
      <div className="min-w-0">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase">Voice control listening</p>
        <p className="font-mono text-[10px] text-zinc-400 truncate">{heard ? `Heard: "${heard}"` : 'Try: "scroll down" · "click submit" · "focus email" · "type hello"'}</p>
      </div>
    </div>
  );
}
